import { SectionEyebrow } from "../components/CommonComponents";
import { ArrowUpRight } from "../components/CommonComponents";

const CourseWhyChoose = ({ course }) => {
  const items = [
    {
      number: "01",
      eyebrow: "Relevant",
      title: "Industry-Aligned Learning",
      description:
        "Engage with academic concepts and contemporary practices designed to reflect evolving professional expectations.",
    },
    {
      number: "02",
      eyebrow: "Applied",
      title: "Learning Beyond Theory",
      description:
        "Strengthen understanding through projects, assignments, practical activities and application-oriented experiences.",
    },
    {
      number: "03",
      eyebrow: "Progressive",
      title: "Professional Development",
      description:
        "Develop communication, analytical thinking, problem-solving and other capabilities that complement academic learning.",
    },
    {
      number: "04",
      eyebrow: "Future Ready",
      title: "A Foundation for What’s Next",
      description:
        "Build knowledge and capabilities that can support career progression, higher studies and continued professional learning.",
    },
  ];

  return (
    <section
      id="why-choose"
      aria-labelledby="why-choose-heading"
      className="relative overflow-hidden bg-ivory py-20 lg:py-28"
    >
      {/* Decorative background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 hidden h-full w-[8%] border-r border-navy/10 lg:block"
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-0 hidden h-full w-[8%] border-l border-navy/10 lg:block"
      />

      <div className="container relative z-[1] mx-auto">

        {/* =========================================
            FULL WIDTH SECTION HEADING
        ========================================== */}
        <div className="grid gap-8 border-b border-navy/15 pb-12 lg:grid-cols-[1fr_auto] lg:items-end lg:gap-16 lg:pb-14">
          <div>
            <SectionEyebrow>
              Why Choose This Programme
            </SectionEyebrow>

            <h2
              id="why-choose-heading"
              className="mt-6 max-w-[970px] font-serif text-[clamp(42px,5vw,72px)] font-normal leading-[1.03] tracking-[-0.04em] text-navy"
            >
              An education designed around{" "}
              <em className="font-normal italic text-burgundy">
                where you want to go.
              </em>
            </h2>
          </div>

          <div className="hidden items-center gap-3 pb-2 lg:flex">
            <span className="text-sm font-bold uppercase tracking-[0.14em] text-navy/45">
              ISBM University
            </span>

            <span className="h-2 w-2 rotate-45 bg-gold" />
          </div>
        </div>

        {/* =========================================
            MAIN ARCHITECTURAL GRID
        ========================================== */}
        <div className="mt-12 grid border border-navy/15 bg-white lg:grid-cols-[1fr_1.16fr_1fr]">

          {/* LEFT COLUMN */}
          <div className="grid md:grid-cols-2 lg:grid-cols-1">
            {items.slice(0, 2).map((item, index) => (
              <article
                key={item.number}
                className={`
                  group
                  relative
                  min-h-[300px]
                  overflow-hidden
                  p-7
                  transition-colors
                  duration-500
                  hover:bg-[#FDF7FC]
                  md:p-9
                  lg:min-h-[330px]
                  lg:p-10

                  ${index === 0 ? "border-b border-navy/15" : ""}

                  max-lg:border-b
                  md:max-lg:border-r
                  md:max-lg:last:border-r-0
                `}
              >
                {/* number + category */}
                <div className="flex items-center justify-between gap-5">
                  <span className="font-serif text-[30px] leading-none text-gold-dark">
                    {item.number}
                  </span>

                  <span className="text-sm font-bold uppercase tracking-[0.14em] text-burgundy">
                    {item.eyebrow}
                  </span>
                </div>

                <div className="mt-12">
                  <h3 className="max-w-[300px] font-serif text-[29px] font-normal leading-[1.16] text-navy">
                    {item.title}
                  </h3>

                  <p className="mt-4 max-w-[330px] text-base leading-[1.75] text-ink/60">
                    {item.description}
                  </p>
                </div>

                {/* corner interaction */}
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 right-0 grid h-12 w-12 place-items-center border-l border-t border-navy/15 text-navy transition-all duration-300 group-hover:border-burgundy group-hover:bg-burgundy group-hover:text-white"
                >
                  ↗
                </span>

                {/* top hover accent */}
                <span
                  aria-hidden="true"
                  className="absolute left-0 top-0 h-[3px] w-0 bg-burgundy transition-all duration-500 group-hover:w-full"
                />
              </article>
            ))}
          </div>

          {/* =========================================
              CENTRAL COURSE PANEL
          ========================================== */}
          <div className="relative flex min-h-[560px] flex-col justify-between overflow-hidden bg-burgundy px-7 py-10 text-white md:px-12 md:py-12 lg:min-h-[660px] lg:px-12 lg:py-14">

            {/* subtle Tailwind gradient */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.07] via-transparent to-black/10"
            />

            {/* circles */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -right-[110px] -top-[110px] h-[300px] w-[300px] rounded-full border border-white/10"
            />

            <span
              aria-hidden="true"
              className="pointer-events-none absolute -right-[45px] -top-[45px] h-[170px] w-[170px] rounded-full border border-gold/30"
            />

            {/* top */}
            <div className="relative z-[1]">
              <div className="flex items-center justify-between gap-6">
                <span className="text-sm font-bold uppercase tracking-[0.16em] text-gold">
                  The ISBM Advantage
                </span>

                <span className="grid h-11 w-11 place-items-center rounded-full border border-white/25">
                  <span className="h-2 w-2 rotate-45 bg-gold" />
                </span>
              </div>
            </div>

            {/* middle */}
            <div className="relative z-[1] my-14">
              <span className="block text-sm font-semibold uppercase tracking-[0.14em] text-white/55">
                Your Programme
              </span>

              <h3 className="mt-5 font-serif text-[clamp(36px,3.5vw,52px)] font-normal leading-[1.08] tracking-[-0.03em]">
                {course?.title || "Your Chosen Programme"}
              </h3>

              <div className="my-8 h-px w-[90px] bg-gold" />

              <p className="max-w-[450px] text-lg leading-[1.75] text-white/75">
                A learning journey that connects{" "}
                <strong className="font-medium text-white">
                  knowledge, application and professional development
                </strong>{" "}
                to help you prepare for the opportunities ahead.
              </p>
            </div>

            {/* bottom */}
            <div className="relative z-[1]">
              <div className="mb-6 flex items-center gap-4">
                <span className="h-px flex-1 bg-white/20" />

                <span className="font-serif text-[22px] italic text-gold">
                  Learn with purpose
                </span>
              </div>

              <a
                href="#program-highlights"
                className="group flex min-h-[58px] w-full items-center justify-between bg-white px-6 text-sm font-bold text-navy transition-colors duration-300 hover:bg-gold"
              >
                Explore Programme Highlights

                <ArrowUpRight className="h-[19px] w-[19px] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>

            {/* decorative huge character */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute bottom-[65px] right-[-12px] font-serif text-[170px] leading-none text-white/[0.025]"
            >
              W
            </span>
          </div>

          {/* RIGHT COLUMN */}
          <div className="grid md:grid-cols-2 lg:grid-cols-1">
            {items.slice(2, 4).map((item, index) => (
              <article
                key={item.number}
                className={`
                  group
                  relative
                  min-h-[300px]
                  overflow-hidden
                  p-7
                  transition-colors
                  duration-500
                  hover:bg-[#FDF7FC]
                  md:p-9
                  lg:min-h-[330px]
                  lg:p-10

                  ${index === 0 ? "border-b border-navy/15" : ""}

                  max-lg:border-t
                  md:max-lg:border-r
                  md:max-lg:last:border-r-0
                `}
              >
                <div className="flex items-center justify-between gap-5">
                  <span className="font-serif text-[30px] leading-none text-gold-dark">
                    {item.number}
                  </span>

                  <span className="text-sm font-bold uppercase tracking-[0.14em] text-burgundy">
                    {item.eyebrow}
                  </span>
                </div>

                <div className="mt-12">
                  <h3 className="max-w-[300px] font-serif text-[29px] font-normal leading-[1.16] text-navy">
                    {item.title}
                  </h3>

                  <p className="mt-4 max-w-[330px] text-base leading-[1.75] text-ink/60">
                    {item.description}
                  </p>
                </div>

                <span
                  aria-hidden="true"
                  className="absolute bottom-0 right-0 grid h-12 w-12 place-items-center border-l border-t border-navy/15 text-navy transition-all duration-300 group-hover:border-burgundy group-hover:bg-burgundy group-hover:text-white"
                >
                  ↗
                </span>

                <span
                  aria-hidden="true"
                  className="absolute left-0 top-0 h-[3px] w-0 bg-burgundy transition-all duration-500 group-hover:w-full"
                />
              </article>
            ))}
          </div>
        </div>

        {/* =========================================
            BOTTOM VALUE STATEMENT
        ========================================== */}
        <div className="grid border-x border-b border-navy/15 bg-white lg:grid-cols-[auto_1fr_auto] lg:items-center">

          <div className="border-b border-navy/15 px-7 py-7 lg:border-b-0 lg:border-r lg:px-10">
            <span className="block text-sm font-bold uppercase tracking-[0.14em] text-burgundy">
              The Goal
            </span>

            <strong className="mt-1 block font-serif text-[24px] font-normal text-navy">
              Your Progress
            </strong>
          </div>

          <p className="px-7 py-7 text-base leading-[1.7] text-ink/60 lg:px-10">
            Build a strong academic foundation while developing the practical
            understanding and professional perspective needed to move forward
            with confidence.
          </p>

          <div className="flex items-center gap-3 border-t border-navy/15 px-7 py-7 lg:border-l lg:border-t-0 lg:px-10">
            <span className="h-2 w-2 rounded-full bg-gold" />
            <span className="text-sm font-bold uppercase tracking-[0.12em] text-navy">
              Knowledge → Capability
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CourseWhyChoose;